/**
 * @file src/components/VidButtonBank/VidButtonBank.styles.ts
 * @exports AccessibilityCloseButton, AccessibilityMenuHeader, AccessibilityMenuPanel, AccessibilityMenuRow, AccessibilityOverlay, BottomButton, ButtonBankWrapper, SingleButton, ToggleLabelHelper, TopButton
 * @summary Styled-components for the VidButtonBank accessibility and support buttons.
 */

import { colors } from "@cerebruminc/cerebellum";
import styled from "styled-components";

/** Icon size (px) for the button bank icons on desktop viewports. */
export const ICON_SIZE = 26;
/** Icon size (px) for the button bank icons on mobile viewports (≤ 450 px). */
export const ICON_SIZE_MOBILE = 22;
const BORDER_RADIUS = 9;

/** Wrapper that positions the button bank fixed to the right edge of the screen. */
export const ButtonBankWrapper = styled.div<{ $buttonOffset?: string }>`
	bottom: ${({ $buttonOffset }) => $buttonOffset || "40px"};
	display: flex;
	flex-direction: column;
	overflow: clip;
	position: fixed;
	right: 0;
	z-index: 199;

	@media screen and (max-width: 319px) {
		bottom: ${({ $buttonOffset }) => $buttonOffset || "10px"};
	}
`;

const BankButtonBase = styled.button<{ $highContrast?: boolean }>`
	align-items: center;
	background-color: ${({ $highContrast }) => ($highContrast ? colors.WHITE : colors.BLUE_5)};
	border: 1px solid  ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.WHITE)};
	cursor: pointer;
	display: flex;
	height: 50px;
	justify-content: center;
	transition: transform 0.2s ease, background-color 0.2s ease;
	transform: translateX(8px);
	width: 63px;

	&:hover {
		background-color: ${({ $highContrast }) => ($highContrast ? colors.WHITE : colors.BLUE_10)};
		transform: translateX(2px);
	}

	@media screen and (max-width: 450px) {
		height: 40px;
		width: 48px;
	}

	svg {
		height: ${ICON_SIZE}px;
		margin-left: -2px;
		width: ${ICON_SIZE}px;

		@media screen and (max-width: 450px) {
			height: ${ICON_SIZE_MOBILE}px;
			width: ${ICON_SIZE_MOBILE}px;
		}
	}
`;

export const TopButton = styled(BankButtonBase)`
	border-radius: ${BORDER_RADIUS}px 0 0 0;
`;

export const BottomButton = styled(BankButtonBase)`
	border-radius: 0 0 0 ${BORDER_RADIUS}px;
`;

export const SingleButton = styled(BankButtonBase)`
	border-radius: ${BORDER_RADIUS}px 0 0 ${BORDER_RADIUS}px;
`;

/** Full-viewport semi-transparent blue overlay shown behind the accessibility popover. */
export const AccessibilityOverlay = styled.div<{ $show: boolean }>`
	background-color: var(--mantine-color-blue-7);
	display: ${({ $show }) => ($show ? "block" : "none")};
	inset: 0;
	opacity: 0.5;
	position: fixed;
	z-index: 198;
`;

/** White panel that wraps the accessibility menu content inside the SlideIn. */
export const AccessibilityMenuPanel = styled.div`
	background-color: ${colors.WHITE};
	border-radius: 20px;
	box-shadow: 0 6px 18px 0 rgba(146, 151, 182, 0.39);
	box-sizing: border-box;
	display: flex;
	flex-flow: column nowrap;
	gap: 16px;
	padding: 24px;
	position: relative;
	width: 100%;
`;

/** Header text at the top of the accessibility menu. */
export const AccessibilityMenuHeader = styled.h2`
	color: ${colors.BLACK};
	font-size: 20px;
	font-weight: 800;
	line-height: 27px;
	margin: 0;
	padding-right: 30px;
`;

/** Close button positioned in the upper-right corner of the accessibility menu panel. */
export const AccessibilityCloseButton = styled.button`
	background: transparent;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	opacity: 0.6;
	position: absolute;
	right: 24px;
	top: 24px;

	&:focus-visible {
		opacity: 1;
		outline: 2px solid ${colors.BLUE_100};
		outline-offset: 2px;
	}

	&:hover {
		opacity: 1;
	}
`;

/** Row wrapper for each toggle item in the accessibility menu. */
export const AccessibilityMenuRow = styled.div`
	cursor: pointer;

	/* Override ToggleItem's inline-block label to prevent text wrapping below the toggle */
	> div > label {
		align-items: flex-start;
		display: flex;
		gap: 4px;
	}
`;

/** Helper description text rendered below each toggle label. */
export const ToggleLabelHelper = styled.span`
	color: ${colors.BLACK};
	display: block;
	font-size: 13px;
	font-weight: 400;
	line-height: 18px;
	margin-top: 2px;
`;
